// Static, data-free thumbnail of a dashboard widget shown in the add-widget
// dialog. Decorative only (aria-hidden): the parent renders the label. Each
// schematic mirrors the block structure of its widget in pages/Dashboard.tsx
// with skeleton shapes, never digits, so nothing reads as a real figure.
import React from 'react';
import type { DashboardWidgetId } from './dashboardWidgets';
import './DashboardWidgetPreview.css';

type Tone = 'blue' | 'orange' | 'green' | 'purple' | 'danger' | 'neutral';

const Line: React.FC<{ width?: 'short' | 'mid' | 'long' }> = ({ width = 'mid' }) => (
  <span className={`dwp-line dwp-line--${width}`} />
);

const TableSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <div className="dwp-table">
    <div className="dwp-table__row dwp-table__row--head">
      <Line width="short" /><Line width="short" /><Line width="short" /><Line width="short" />
    </div>
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="dwp-table__row">
        <Line /><Line width="long" /><Line width="short" /><Line />
      </div>
    ))}
  </div>
);

const KpiPreview: React.FC = () => {
  const tones: Tone[] = ['blue', 'orange', 'green', 'purple', 'danger', 'neutral'];
  return (
    <div className="dwp-kpi">
      {tones.map((tone) => (
        <div key={tone} className="dwp-tile">
          <span className={`dwp-chip dwp-chip--${tone}`} />
          <span className="dwp-stack">
            <Line width="short" />
            <Line width="long" />
          </span>
        </div>
      ))}
    </div>
  );
};

const PaymentsPreview: React.FC = () => (
  <div className="dwp-column">
    <div className="dwp-pair">
      <div className="dwp-summary dwp-summary--success"><Line width="short" /><Line /></div>
      <div className="dwp-summary dwp-summary--danger"><Line width="short" /><Line /></div>
    </div>
    <TableSkeleton rows={2} />
  </div>
);

const FinancePreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    <div className="dwp-finance">
      {[0, 1, 2].map((key) => (
        <div key={key} className="dwp-metric">
          <Line width="short" />
          <span className="dwp-bar" />
          <Line />
        </div>
      ))}
    </div>
  </div>
);

const PrioritiesPreview: React.FC = () => {
  const tones: Tone[] = ['danger', 'orange', 'blue', 'green'];
  return (
    <div className="dwp-column">
      <Line width="short" />
      <div className="dwp-grid-2">
        {tones.map((tone) => (
          <div key={tone} className="dwp-row">
            <span className={`dwp-chip dwp-chip--${tone}`} />
            <Line />
            <span className="dwp-badge" />
          </div>
        ))}
      </div>
    </div>
  );
};

const WeekPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    <div className="dwp-week">
      {[0, 1, 2, 3, 4, 5, 6].map((day) => (
        <div key={day} className={day === 1 || day === 4 ? 'dwp-day dwp-day--active' : 'dwp-day'}>
          <span className="dwp-day__dot" />
        </div>
      ))}
    </div>
    <div className="dwp-footer"><Line width="long" /></div>
  </div>
);

const StatusPreview: React.FC = () => {
  const tones: Tone[] = ['blue', 'blue', 'orange', 'purple', 'danger', 'neutral'];
  return (
    <div className="dwp-column">
      <Line width="short" />
      <div className="dwp-status">
        {tones.map((tone, index) => (
          <div key={`${tone}-${index}`} className={`dwp-status-card dwp-status-card--${tone}`}>
            <Line width="short" />
            <span className="dwp-bar dwp-bar--small" />
          </div>
        ))}
      </div>
    </div>
  );
};

const RisksPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    <div className="dwp-row dwp-row--danger"><span className="dwp-chip dwp-chip--danger" /><Line width="long" /></div>
    <div className="dwp-row"><span className="dwp-chip dwp-chip--orange" /><Line width="long" /></div>
    <div className="dwp-row"><span className="dwp-chip dwp-chip--neutral" /><Line width="long" /></div>
    <div className="dwp-subheader"><Line width="mid" /></div>
  </div>
);

const RecentPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    <TableSkeleton rows={4} />
  </div>
);

const WorkQueuePreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    {[0, 1, 2, 3].map((index) => (
      <div key={index} className="dwp-row">
        <span className="dwp-stack"><Line width="short" /><Line width="mid" /></span>
        <span className="dwp-chip dwp-chip--blue dwp-chip--pill" />
      </div>
    ))}
  </div>
);

const UpcomingPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    {[0, 1, 2, 3].map((index) => (
      <div key={index} className="dwp-row">
        <span className="dwp-stack"><Line width="short" /><Line width="mid" /></span>
        <Line width="short" />
      </div>
    ))}
  </div>
);

const StatusChartPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    {[0.9, 0.6, 0.4, 0.25].map((share, index) => (
      <div key={index} className="dwp-bar-row">
        <Line width="short" />
        <span className="dwp-bar"><span className="dwp-bar__fill" style={{ width: `${share * 100}%` }} /></span>
      </div>
    ))}
  </div>
);

const QuickActionsPreview: React.FC = () => (
  <div className="dwp-column">
    <Line width="short" />
    <div className="dwp-quick">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <span key={index} className="dwp-quick__pill"><span className="dwp-chip dwp-chip--neutral" /><Line width="short" /></span>
      ))}
    </div>
  </div>
);

const ListRowsPreview: React.FC<{ tone: Tone }> = ({ tone }) => (
  <div className="dwp-column">
    <Line width="short" />
    {[0, 1, 2, 3].map((index) => (
      <div key={index} className="dwp-row">
        <span className={`dwp-chip dwp-chip--${index === 0 ? tone : 'neutral'}`} />
        <span className="dwp-stack"><Line width="mid" /><Line width="short" /></span>
        <Line width="short" />
      </div>
    ))}
  </div>
);

const UpcomingTransportsPreview: React.FC = () => <ListRowsPreview tone="blue" />;
const ExpectedDeliveriesPreview: React.FC = () => <ListRowsPreview tone="orange" />;
const MyTasksPreview: React.FC = () => <ListRowsPreview tone="danger" />;

const PREVIEWS: Record<DashboardWidgetId, React.FC> = {
  kpi: KpiPreview,
  payments: PaymentsPreview,
  finance: FinancePreview,
  priorities: PrioritiesPreview,
  week: WeekPreview,
  status: StatusPreview,
  risks: RisksPreview,
  recent: RecentPreview,
  workQueue: WorkQueuePreview,
  upcoming: UpcomingPreview,
  statusChart: StatusChartPreview,
  quickActions: QuickActionsPreview,
  upcomingTransports: UpcomingTransportsPreview,
  expectedDeliveries: ExpectedDeliveriesPreview,
  myTasks: MyTasksPreview,
};

const DashboardWidgetPreview: React.FC<{ id: DashboardWidgetId }> = ({ id }) => {
  const Preview = PREVIEWS[id];
  return (
    <div className={`dashboard-widget-preview dashboard-widget-preview--${id}`} aria-hidden="true">
      <Preview />
    </div>
  );
};

export default DashboardWidgetPreview;
